import React, { useEffect, useRef, useState } from "react";

import isEmpty from "lodash.isempty";
import { observer } from "mobx-react";
import { useSearchParams } from "../../../hooks/useNavigation";
import PropTypes from "prop-types";
import { useTheme } from "styled-components";

import { useApi } from "../../../../contexts/ApiContext";
import { useStores } from "../../../../contexts/StoreContext";
import useIsHydrated from "../../../hooks/useIsHydrated";
import CustomSelect from "../../../CustomSelect/CustomSelect";
import LocationControl from "../../LocationSelect/LocationControl";
import getPosition from "../../LocationSelect/getPosition";

function LocationFilter(props) {
  const { onSelect, placeholder, initialValue, distance, searchIconColour } =
    props;
  const { configStore, filtersStore } = useStores();
  const isHydrated = useIsHydrated();
  const searchParams = useSearchParams();
  const searchObj = Object.fromEntries(searchParams.entries());

  const urlLocation = searchObj["location[label]"];

  const api = useApi();
  const { config } = configStore;
  const { frontendFilters, setFromFilter, applyFilters } = filtersStore;

  const asyncSelectRef = useRef(null);

  const [geolocationStatusText, setGeolocationStatusText] = useState(null);
  const [isGeolocationInProgress, setIsGeolocationInProgress] = useState(false);
  const [selectedDistance, setSelectedDistance] = useState(distance || 5);

  const distanceOptions = [
    { label: "1 mi", value: 1 },
    { label: "5 mi", value: 5 },
    { label: "10 mi", value: 10 },
    { label: "25 mi", value: 25 },
    { label: "50 mi", value: 50 },
    { label: "100 mi", value: 100 },
  ];

  const handleSetLocation = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setGeolocationStatusText("Wait, we're trying to locate you...");
    setIsGeolocationInProgress(true);

    getPosition()
      .then((geoLocation) => {
        api
          .reverseGeocode({
            lat: geoLocation.coords.latitude,
            long: geoLocation.coords.longitude,
          })
          .then((geocodedLocation) => {
            const address =
              geocodedLocation?.data[0]?.address?.freeformAddress ||
              geocodedLocation?.data[0]?.address?.streetNameAndNumber ||
              "Your localization";

            const locationValue = {
              coords: {
                lat: geoLocation.coords.latitude,
                lng: geoLocation.coords.longitude,
              },
              label: address,
              value: address,
              distance: selectedDistance,
            };

            asyncSelectRef.current.setValue({
              coordsMode: true,
              label: address,
              lat: geoLocation.coords.latitude,
              long: geoLocation.coords.longitude,
              value: locationValue,
            });

            setFromFilter("location", [locationValue]);
            applyFilters();

            setGeolocationStatusText(null);
            setIsGeolocationInProgress(false);
          });
      })
      .catch((err) => {
        setGeolocationStatusText(
          "We can't reach your location. Maybe check permissions?"
        );
        setIsGeolocationInProgress(false);
        setTimeout(() => {
          setGeolocationStatusText(null);
        }, 5000);
        // eslint-disable-next-line no-console
        console.error("Error message ->", err.message);
      });
  };

  const loadOptions = (inputValue) => {
    if (inputValue.length >= 3) {
      return (
        api
          .searchLocation(inputValue, config.location || "GB")
          .then((response) => {
            return response.data.map((entry) => {
              const label = [
                entry.addressLine1,
                entry.addressLine2,
                entry.addressLine3,
                entry.city,
                entry.county,
                entry.postCode,
              ]
                .filter(Boolean)
                .join(", ");

              return {
                coordsMode: true,
                label,
                lat: entry.lat,
                long: entry.long,
                value: {
                  coords: { lat: entry.lat, lng: entry.long },
                  label,
                  value: entry,
                  distance: selectedDistance,
                },
              };
            });
          })
          // eslint-disable-next-line no-console
          .catch((err) => console.log(err))
      );
    }

    return Promise.resolve();
  };

  const handleLocationChange = (selectedLocation) => {
    if (selectedLocation) {
      const locationWithDistance = {
        ...selectedLocation.value,
        distance: selectedDistance,
      };
      setFromFilter("location", [locationWithDistance]);
      applyFilters();
    } else {
      setFromFilter("location", []);
      applyFilters();
    }

    if (onSelect) {
      onSelect(selectedLocation);
    }
  };

  const handleDistanceChange = (selectedDistanceOption) => {
    setSelectedDistance(selectedDistanceOption.value);

    const currentLocation = frontendFilters.location?.[0];
    if (currentLocation) {
      const updatedLocation = {
        ...currentLocation,
        distance: selectedDistanceOption.value,
      };
      setFromFilter("location", [updatedLocation]);
      applyFilters();
    }
  };

  useEffect(() => {
    const load = async () => {
      const locationLabel =
        frontendFilters.location?.label ||
        frontendFilters.locationLabel ||
        initialValue;

      if (!isEmpty(locationLabel)) {
        const options = await loadOptions(locationLabel);

        const filteredOption = options.find((location) =>
          location.label.includes(locationLabel)
        );

        if (filteredOption) {
          asyncSelectRef.current.setValue({
            label: filteredOption.label,
            value: filteredOption,
          });
        }
      }
    };
    load();
  }, [JSON.stringify(frontendFilters), initialValue]);

  const theme = useTheme();

  useEffect(() => {
    if (!frontendFilters.locationLabel && isEmpty(frontendFilters.location)) {
      asyncSelectRef.current.clearValue();
    }
  }, [JSON.stringify(frontendFilters)]);

  const locationSelectStyles = {
    control: (provided) => ({
      ...provided,
      background: "white",
      borderRadius: "10px",
      boxShadow: "none",
      fontWeight: "400",
      height: "100%",
      textColor: "black",
      border: "1px solid #ccc",
    }),
    menuList: (provided) => ({
      ...provided,
      padding: "0",
      color: "black",
    }),
    option: (provided) => ({
      ...provided,
      color: "black",
      height: "100%",
    }),
    placeholder: (provided) => ({
      ...provided,
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "black",
    }),
  };

  const distanceSelectStyles = {
    container: (provided) => ({
      ...provided,
      width: "120px",
      minWidth: "120px",
    }),
    control: (provided) => ({
      ...provided,
      background: "white",
      borderRadius: "10px",
      boxShadow: "none",
      fontWeight: "400",
      height: "100%",
      textColor: "black",
      border: "1px solid #ccc",
    }),
    menuList: (provided) => ({
      ...provided,
      padding: "0",
      color: "black",
    }),
    option: (provided) => ({
      ...provided,
      color: "black",
      height: "100%",
    }),
    placeholder: (provided) => ({
      ...provided,
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "black",
    }),
  };

  return (
    <div
      style={{
        display: "flex",
        gap: "8px",
        alignItems: "center",
        width: "100%",
      }}
    >
      <CustomSelect
        aria-label="Location"
        className="location-input flex-grow-1"
        components={{
          Control: LocationControl,
          DropdownIndicator: () => null,
        }}
        data-testId="location-input"
        defaultInputValue={initialValue || urlLocation}
        instanceId="locationInput"
        isAsync
        isClearable
        isGeolocationInProgress={isGeolocationInProgress}
        isSearchable
        loadOptions={loadOptions}
        locationButtonConfig={{
          color: searchIconColour || theme.primaryColor,
        }}
        menuPortalTarget={isHydrated ? document.body : null}
        noOptionsMessage={() => "Type to start searching"}
        onChange={(value, ...rest) => {
          return handleLocationChange(value, rest);
        }}
        onLocationClick={handleSetLocation}
        placeholder={geolocationStatusText || placeholder || "Town or Postcode"}
        selectRef={asyncSelectRef}
        styles={locationSelectStyles}
      />
      <CustomSelect
        aria-label="Distance"
        className="distance-select"
        data-testId="distance-select"
        instanceId="distanceSelect"
        isClearable={false}
        isSearchable={false}
        onChange={handleDistanceChange}
        options={distanceOptions}
        placeholder="Distance"
        styles={distanceSelectStyles}
        value={distanceOptions.find(
          (option) => option.value === selectedDistance
        )}
      />
    </div>
  );
}

export default observer(LocationFilter);

LocationFilter.propTypes = {
  initialValue: PropTypes.string,
  onSelect: PropTypes.func,
  placeholder: PropTypes.string,
  styles: PropTypes.shape({}),
  distance: PropTypes.number,
  searchIconColour: PropTypes.string,
};

LocationFilter.defaultProps = {
  initialValue: "",
  onSelect: () => {},
  placeholder: "Town or Postcode",
  styles: {},
  distance: null,
  searchIconColour: null,
};
